import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Pencil, Plus, Star, Trash2 } from "lucide-react";
import { money, imgUrl, useCategories, refreshCategories } from "@/components/landing/useProducts";
import { ProductForm } from "./ProductForm";
import { CategoryCover } from "./CategoryCover";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ProductsTab = ({ headers }) => {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(undefined);
  const initialCats = useCategories();
  const [cats, setCats] = useState(null);
  const [newCat, setNewCat] = useState("");
  const CATEGORIES = cats || initialCats;

  const addCategory = async (e) => {
    e.preventDefault();
    if (!newCat.trim()) return;
    try {
      await axios.post(`${API}/admin/categories`, { label: newCat.trim() }, { headers });
      setCats(await refreshCategories());
      setNewCat("");
      toast.success("Category added");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add category.");
    }
  };
  const removeCategory = async (c) => {
    if (!window.confirm(`Delete category "${c.label}"?`)) return;
    try {
      await axios.delete(`${API}/admin/categories/${c.id}`, { headers });
      setCats(await refreshCategories());
      toast.success("Category deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete category.");
    }
  };

  useEffect(() => {
    axios.get(`${API}/admin/products`, { headers }).then((r) => setItems(r.data)).catch(() => toast.error("Could not load products."));
  }, [headers]);

  const saved = (p) => {
    setItems((list) => (list.some((x) => x.id === p.id) ? list.map((x) => (x.id === p.id ? p : x)) : [...list, p]));
    setEditing(undefined);
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? Its Stripe prices will be retired.`)) return;
    try {
      await axios.delete(`${API}/admin/products/${p.id}`, { headers });
      setItems((list) => list.filter((x) => x.id !== p.id));
      toast.success("Product deleted");
    } catch {
      toast.error("Could not delete product.");
    }
  };
  const toggleFeatured = async (p) => {
    try {
      const r = await axios.put(`${API}/admin/products/${p.id}`, { ...p, featured: !p.featured }, { headers });
      saved(r.data);
    } catch {
      toast.error("Could not update.");
    }
  };

  if (items === null) return <div className="py-24 flex justify-center"><Loader2 className="animate-spin text-[#5f5e5a]" /></div>;

  return (
    <div data-testid="admin-products" className="mt-8">
      <div className="flex items-center justify-between gap-4 mb-5">
        <p className="font-mono-d text-[11px] uppercase tracking-[0.2em] text-[#5f5e5a]">{items.length} products · {items.filter((p) => p.featured).length} featured</p>
        <button data-testid="product-new-button" onClick={() => setEditing(null)} className="h-11 px-5 rounded-full bg-[#0a0a0b] hover:bg-[#ff2a85] text-white font-mono-d text-[10px] uppercase tracking-[0.2em] flex items-center gap-2 transition-colors">
          <Plus size={14} /> New product
        </button>
      </div>

      <form onSubmit={addCategory} data-testid="category-form" className="mb-8 flex gap-2">
        <input data-testid="category-name-input" value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="New category name (e.g. Outerwear)"
          className="flex-1 h-11 px-4 rounded-full bg-[#faf9f6] border border-[#e3e2dc] text-sm outline-none focus:border-[#ff2a85]/60" />
        <button data-testid="category-add-button" type="submit" className="h-11 px-5 rounded-full border border-[#0a0a0b] hover:bg-[#0a0a0b] hover:text-white font-mono-d text-[10px] uppercase tracking-[0.2em] transition-colors">Add category</button>
      </form>

      {CATEGORIES.map((c) => {
        const rows = items.filter((p) => p.category === c.id);
        return (
          <section key={c.id} data-testid={`products-group-${c.id}`} className="mb-8">
            <h3 className="font-display font-extrabold uppercase text-sm tracking-wide mb-3 flex items-center gap-2">
              {c.label} <span className="font-mono-d text-[10px] text-[#5f5e5a] font-normal">({rows.length})</span>
              {!rows.length && (
                <button data-testid={`category-delete-${c.id}`} onClick={() => removeCategory(c)} className="ml-2 font-mono-d text-[9px] uppercase tracking-[0.15em] text-[#5f5e5a] hover:text-[#ff2a85] font-normal">Delete category</button>
              )}
            </h3>
            <CategoryCover category={c} headers={headers} />
            {!rows.length ? (
              <p className="text-xs text-[#5f5e5a] font-mono-d uppercase tracking-[0.15em] border border-dashed border-[#e3e2dc] rounded-xl p-4">Empty — add a product to this category</p>
            ) : (
              <div className="border border-[#e3e2dc] rounded-2xl overflow-hidden bg-[#faf9f6] divide-y divide-[#e3e2dc]">
                {rows.map((p) => (
                  <div key={p.id} data-testid={`product-row-${p.slug}`} className={`flex items-center gap-4 px-4 py-3 ${p.active ? "" : "opacity-50"}`}>
                    <img src={imgUrl(p.image)} alt="" className="h-14 w-11 rounded-md object-cover object-top bg-[#f3f2ec]" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium truncate">{p.name}</span>
                        {p.badge && <span className="font-mono-d text-[8px] uppercase tracking-[0.15em] bg-[#0a0a0b] text-white px-2 py-0.5 rounded-full">{p.badge}</span>}
                        {!p.active && <span className="font-mono-d text-[8px] uppercase tracking-[0.15em] text-[#5f5e5a]">hidden</span>}
                      </div>
                      <p className="text-xs text-[#5f5e5a] truncate">
                        {p.options.map((o) => `${o.label} ${money(o.price)}${o.regular ? ` (reg ${money(o.regular)})` : ""}`).join(" · ")}
                        {p.colors?.length ? ` · ${p.colors.length} colours` : ""}
                      </p>
                      {p.stock != null && (
                        <p data-testid={`product-stock-${p.slug}`} className={`text-[10px] font-mono-d uppercase tracking-[0.15em] mt-0.5 ${p.remaining === 0 ? "text-[#ff2a85]" : "text-[#5f5e5a]"}`}>
                          {p.sold} sold · {p.remaining} of {p.stock} left
                        </p>
                      )}
                    </div>
                    <button data-testid={`product-feature-${p.slug}`} onClick={() => toggleFeatured(p)} aria-label="Toggle featured"
                      className={`h-9 w-9 rounded-full flex items-center justify-center transition-colors ${p.featured ? "bg-[#ff2a85] text-white" : "text-[#5f5e5a] hover:text-[#ff2a85]"}`}>
                      <Star size={15} fill={p.featured ? "currentColor" : "none"} />
                    </button>
                    <button data-testid={`product-edit-${p.slug}`} onClick={() => setEditing(p)} aria-label="Edit" className="h-9 w-9 rounded-full flex items-center justify-center text-[#5f5e5a] hover:text-[#0a0a0b]"><Pencil size={15} /></button>
                    <button data-testid={`product-delete-${p.slug}`} onClick={() => remove(p)} aria-label="Delete" className="h-9 w-9 rounded-full flex items-center justify-center text-[#5f5e5a] hover:text-[#ff2a85]"><Trash2 size={15} /></button>
                  </div>
                ))}
              </div>
            )}
          </section>
        );
      })}

      {editing !== undefined && <ProductForm product={editing} headers={headers} onClose={() => setEditing(undefined)} onSaved={saved} allProducts={items || []} />}
    </div>
  );
};
